import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import baseUrl from "../api/utils";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import LinkPaymentModal from "./LinkPaymentModal";

const PAYMENT_MODE_OPTIONS = ["Cash", "Card", "UPI", "Cheque", "Bank Transfer", "Other"];

const fmt = (n) => (n || n === 0) ? Number(n).toLocaleString("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2
}) : "0.00";

// ═══════════════════════════════════════════════════════════════════════════════
// ADD PAYMENT-IN — matches the Vyapar reference:
//  1. search a party, each result shows their live balance
//  2. selecting one auto-fills Received = their full outstanding balance
//     and auto-links it against their open bill(s)
//  3. LINK PAYMENT lets the cashier review/adjust which bill(s) it applies to
//  4. leftover (if any) is saved as "Unused" — an advance on the customer's account
// ═══════════════════════════════════════════════════════════════════════════════
const AddPaymentInModal = ({ onClose, onSaved }) => {
  const MySwal = withReactContent(Swal);
  const token = sessionStorage.getItem("token");
  const fileInputRef = useRef();
  const partySearchTimer = useRef(null);

  const [receiptNo, setReceiptNo] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);

  // ── party search ──
  const [partyQuery, setPartyQuery] = useState("");
  const [partyResults, setPartyResults] = useState([]);
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null); // { customerId, name, phone, balance }

  // ── payment splits (Cash / Card / etc) ──
  const [splits, setSplits] = useState([{ type: "Cash", amount: "" }]);

  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [received, setReceived] = useState("");
  const [allocations, setAllocations] = useState([]); // [{ saleInvoiceId, linkedAmount }]
  const [showLinkModal, setShowLinkModal] = useState(false);

  const [saving, setSaving] = useState(false);

  const linkedTotal = allocations.reduce((s, a) => s + (parseFloat(a.linkedAmount) || 0), 0);
  const unusedAmount = (parseFloat(received) || 0) - linkedTotal;

  // ── fetch next receipt number on open ──
  useEffect(() => {
    axios.get(`${baseUrl}/getNextPaymentInReceiptNo`, { headers: { Authorization: token } })
      .then((res) => setReceiptNo(res.data))
      .catch(() => setReceiptNo(1));
  }, []);

  // ── party search, debounced ──
  const handlePartySearch = (value) => {
    setPartyQuery(value);
    setSelectedParty(null);
    clearTimeout(partySearchTimer.current);
    if (!value || value.length < 1) {
      setPartyResults([]);
      setShowPartyDropdown(false);
      return;
    }
    partySearchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${baseUrl}/searchPartyBalance`, {
          headers: { Authorization: token },
          params: { keyword: value },
        });
        setPartyResults(res.data || []);
        setShowPartyDropdown(true);
      } catch (err) {
        console.error("Party search failed", err);
      }
    }, 300);
  };

  // ── selecting a party: auto-fill Received with their balance, auto-link it ──
  const handleSelectParty = async (party) => {
    setSelectedParty(party);
    setPartyQuery(party.name);
    setShowPartyDropdown(false);

    const balance = parseFloat(party.balance) || 0;
    setReceived(balance > 0 ? String(balance) : "");

    if (balance > 0) {
      try {
        const res = await axios.get(`${baseUrl}/autoLinkPayment`, {
          headers: { Authorization: token },
          params: { customerId: party.customerId, amount: balance },
        });
        const allocs = (res.data || [])
          .filter((b) => (b.linkedAmount || 0) > 0)
          .map((b) => ({ saleInvoiceId: b.saleInvoiceId, linkedAmount: b.linkedAmount }));
        setAllocations(allocs);
      } catch (err) {
        console.error("Auto link failed", err);
      }
    } else {
      setAllocations([]);
    }
  };

  // ── payment split handlers ──
  const updateSplit = (idx, field, value) => {
    setSplits((prev) => prev.map((s, i) => (i !== idx ? s : { ...s, [field]: value })));
  };
  const addSplit = () => setSplits((prev) => [...prev, { type: "Cash", amount: "" }]);
  const removeSplit = (idx) => setSplits((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  // ── Link Payment modal done ──
  const handleLinkDone = (newAllocations) => {
    setAllocations(newAllocations);
    setShowLinkModal(false);
  };

  const handleSave = async () => {
    if (!selectedParty) {
      MySwal.fire({ icon: "warning", title: "Select a party", text: "Please choose the customer this payment is from." });
      return;
    }
    const amt = parseFloat(received) || 0;
    if (amt <= 0) {
      MySwal.fire({ icon: "warning", title: "Enter an amount", text: "Please enter the amount received." });
      return;
    }
    if (linkedTotal > amt) {
      MySwal.fire({ icon: "warning", title: "Linked amount too high", text: "Linked amount cannot exceed the received amount." });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customerId: selectedParty.customerId,
        paymentDate,
        totalReceived: amt,
        description,
        imagePath: null, // TODO: wire to your existing image-upload endpoint if needed
        splits: splits.filter((s) => s.amount).map((s) => ({ paymentType: s.type, amount: parseFloat(s.amount) })),
        allocations,
      };
      await axios.post(`${baseUrl}/savePaymentIn`, payload, { headers: { Authorization: token } });
      MySwal.fire({ icon: "success", title: "Payment saved", text: `₹${fmt(amt)} recorded for ${selectedParty.name}.` })
        .then(() => { onSaved?.(); onClose?.(); });
    } catch (err) {
      console.error("Save payment-in failed", err);
      MySwal.fire({ icon: "error", title: "Failed to save", text: "Could not save this payment. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "#fff", borderRadius: "8px", width: "700px", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>

          {/* header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #eee" }}>
            <span style={{ fontWeight: "700", fontSize: "17px", color: "#222" }}>Payment-In</span>
            <span onClick={onClose} style={{ cursor: "pointer", fontSize: "18px", color: "#888" }}>✕</span>
          </div>

          <div style={{ display: "flex", padding: "20px 24px", gap: "24px" }}>

            {/* ── LEFT COLUMN ── */}
            <div style={{ flex: 1, position: "relative" }}>
              <label style={{ fontSize: "12px", color: "#1976d2", fontWeight: "600" }}>Party *</label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  value={partyQuery}
                  onChange={(e) => handlePartySearch(e.target.value)}
                  onFocus={() => partyResults.length > 0 && setShowPartyDropdown(true)}
                  placeholder="Search party"
                  style={{ width: "100%", padding: "9px 10px", border: "1px solid #1976d2", borderRadius: "4px", fontSize: "14px", outline: "none", marginTop: "4px", boxSizing: "border-box" }}
                />
                <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#888", fontSize: "11px", pointerEvents: "none" }} />
              </div>
              {selectedParty && (
                <div style={{ fontSize: "12px", color: "#27ae60", fontWeight: "600", marginTop: "4px" }}>
                  BAL: {fmt(selectedParty.balance)}
                </div>
              )}

              {/* party dropdown */}
              {showPartyDropdown && partyResults.length > 0 && (
                <div style={{ position: "absolute", top: "68px", left: 0, right: 0, background: "#fff", border: "1px solid #ddd", borderRadius: "4px", zIndex: 20, maxHeight: "220px", overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                  {partyResults.map((p) => (
                    <div key={p.customerId}
                      onClick={() => handleSelectParty(p)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f5f5f5" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f0f7ff"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                      <div>
                        <div style={{ fontWeight: "600", fontSize: "13px" }}>{p.name}</div>
                        {p.phone && <div style={{ fontSize: "11px", color: "#888" }}>{p.phone}</div>}
                      </div>
                      <span style={{ fontSize: "12px", fontWeight: "700", color: p.balance > 0 ? "#1976d2" : "#aaa", background: p.balance > 0 ? "#e3f2fd" : "#f5f5f5", padding: "2px 8px", borderRadius: "10px" }}>
                        {fmt(p.balance)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* payment mode splits */}
              <div style={{ marginTop: "16px" }}>
                <label style={{ fontSize: "12px", color: "#888", fontWeight: "600" }}>Payment Type</label>
                {splits.map((s, idx) => (
                  <div key={idx} style={{ display: "flex", gap: "8px", marginTop: "6px", alignItems: "center" }}>
                    <select value={s.type} onChange={(e) => updateSplit(idx, "type", e.target.value)}
                      style={{ padding: "7px 8px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", width: "150px" }}>
                      {PAYMENT_MODE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    {splits.length > 1 && (
                      <input type="number" placeholder="Amount" value={s.amount}
                        onChange={(e) => updateSplit(idx, "amount", e.target.value)}
                        style={{ padding: "7px 8px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", width: "100px", textAlign: "right" }} />
                    )}
                    {splits.length > 1 && (
                      <span onClick={() => removeSplit(idx)} style={{ color: "#e74c3c", cursor: "pointer", fontSize: "12px" }}>
                        <i className="fa-solid fa-trash-can" />
                      </span>
                    )}
                  </div>
                ))}
                <button onClick={addSplit} style={{ background: "none", border: "none", color: "#1976d2", fontWeight: "600", fontSize: "13px", cursor: "pointer", padding: "8px 0 0 0", display: "flex", alignItems: "center", gap: "6px" }}>
                  <i className="fa-solid fa-plus" style={{ fontSize: "11px" }} /> Add Payment type
                </button>
              </div>

              {/* description */}
              <div style={{ marginTop: "16px" }}>
                <button onClick={() => document.getElementById("payin-desc-toggle").style.display = "block"}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 14px", background: "#fff", border: "1px solid #e0e0e0", borderRadius: "4px", fontSize: "13px", color: "#888", cursor: "pointer" }}>
                  <i className="fa-regular fa-file-lines" /> ADD DESCRIPTION
                </button>
                <div id="payin-desc-toggle" style={{ display: "none", marginTop: "8px" }}>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                    placeholder="Optional note"
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: "4px", fontSize: "13px", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
              </div>

              {/* image */}
              <div style={{ marginTop: "12px" }}>
                <span onClick={() => fileInputRef.current?.click()} style={{ cursor: "pointer", color: "#888", fontSize: "18px" }}>
                  <i className="fa-solid fa-camera" />
                  {imageFile && <span style={{ fontSize: "12px", marginLeft: "8px", color: "#333" }}>{imageFile.name.substring(0, 20)}</span>}
                </span>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
              </div>
            </div>

            {/* ── RIGHT COLUMN ── */}
            <div style={{ width: "220px" }}>
              <div style={{ marginBottom: "14px" }}>
                <label style={{ fontSize: "12px", color: "#888" }}>Receipt No</label>
                <input type="text" value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", marginTop: "4px", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontSize: "12px", color: "#888" }}>Date</label>
                <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", marginTop: "4px", boxSizing: "border-box" }} />
              </div>

              <div style={{ marginBottom: "8px" }}>
                <label style={{ fontSize: "12px", color: "#888" }}>Received</label>
                <input type="number" value={received}
                  onChange={(e) => setReceived(e.target.value)}
                  disabled={!selectedParty}
                  placeholder="0"
                  style={{ width: "100%", padding: "9px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "15px", fontWeight: "700", marginTop: "4px", boxSizing: "border-box", background: selectedParty ? "#fff" : "#f5f5f5" }} />
              </div>

              {selectedParty && (
                <div style={{ fontSize: "13px", marginBottom: "8px" }}>
                  <span style={{ color: "#333", fontWeight: "700" }}>Unused </span>
                  <span style={{ color: unusedAmount > 0 ? "#c0392b" : "#27ae60", fontWeight: "700" }}>{fmt(unusedAmount)}</span>
                </div>
              )}
            </div>
          </div>

          {/* footer */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", borderTop: "1px solid #eee" }}>
            <button onClick={() => selectedParty && setShowLinkModal(true)} disabled={!selectedParty}
              style={{ padding: "9px 20px", background: selectedParty ? "#27ae60" : "#ccc", color: "#fff", border: "none", borderRadius: "4px", fontWeight: "700", fontSize: "13px", cursor: selectedParty ? "pointer" : "default" }}>
              LINK PAYMENT
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: "9px 32px", background: saving ? "#aaa" : "#1976d2", color: "#fff", border: "none", borderRadius: "4px", fontWeight: "700", fontSize: "13px", cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      {showLinkModal && selectedParty && (
        <LinkPaymentModal
          customerId={selectedParty.customerId}
          partyName={selectedParty.name}
          receivedAmount={received}
          initialAllocations={allocations}
          onCancel={() => setShowLinkModal(false)}
          onDone={handleLinkDone}
        />
      )}
    </>
  );
};

export default AddPaymentInModal;